import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "wedflow_admin_token";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

function Stat({ label, value, hint, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-neutral-200 rounded-[12px] p-6 md:p-8 flex flex-col gap-2"
    >
      <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-500">
        {label}
      </div>
      <div className="font-display text-[40px] md:text-[52px] font-light tracking-[-0.03em] text-neutral-950 wf-tabular leading-none">
        {value}
      </div>
      {hint && <div className="text-[12px] text-neutral-500">{hint}</div>}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(
    () => (typeof window !== "undefined" && localStorage.getItem(TOKEN_KEY)) || ""
  );
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [s, w] = await Promise.all([
        axios.get(`${API}/admin/stats`, auth),
        axios.get(`${API}/admin/waitlist?limit=1000`, auth),
      ]);
      setStats(s.data);
      setEntries(w.data || []);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, token);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) {
        toast.error("Token non valido");
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
      } else {
        toast.error("Errore nel caricamento");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && !authed) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogin = (e) => {
    e.preventDefault();
    if (!token.trim()) return toast.error("Inserisci il token");
    load();
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setStats(null);
    setEntries([]);
  };

  const onExportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/waitlist.csv`, {
        ...auth,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      link.setAttribute("download", `wedflow-waitlist-${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export CSV avviato");
    } catch (e) {
      toast.error("Export fallito");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (e) =>
        e.email?.toLowerCase().includes(q) ||
        e.source?.toLowerCase().includes(q) ||
        e.status?.toLowerCase().includes(q)
    );
  }, [entries, query]);

  if (!authed) {
    return (
      <div
        data-testid="admin-login-screen"
        className="min-h-screen bg-white flex items-center justify-center px-5"
      >
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="w-full max-w-md border border-neutral-200 rounded-[14px] p-8 md:p-10 bg-white"
        >
          <div className="flex items-center gap-2 mb-8">
            <span
              aria-hidden
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: "#c9a86a" }}
            />
            <span className="font-display tracking-[0.28em] text-sm font-medium text-neutral-950">
              WEDFLOW · ADMIN
            </span>
          </div>
          <h1 className="font-display text-[28px] md:text-[36px] tracking-[-0.02em] font-light text-neutral-950">
            Accesso riservato
          </h1>
          <p className="mt-3 text-[14px] text-neutral-600">
            Inserisci il token amministratore per accedere alla lista d'attesa.
          </p>
          <label className="mt-8 block text-[11px] tracking-[0.22em] uppercase text-neutral-500">
            Admin token
          </label>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="admin-token-input"
            placeholder="••••••••••••"
            className="mt-2 w-full bg-white border border-neutral-200 rounded-full px-5 py-3.5 text-[14px] outline-none focus:border-[color:var(--wf-gold)] transition-colors"
            autoComplete="off"
            autoFocus
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="mt-6 w-full wf-btn-primary rounded-full py-3.5 text-[13px] tracking-wide disabled:opacity-60"
          >
            {loading ? "Verifica…" : "Accedi"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="admin-dashboard">
      <header className="border-b border-neutral-200 bg-white">
        <div className="max-w-[1240px] mx-auto px-5 md:px-8 h-16 md:h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              aria-hidden
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: "#c9a86a" }}
            />
            <span className="font-display tracking-[0.28em] text-[13px] md:text-sm font-medium text-neutral-950">
              WEDFLOW · ADMIN
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              data-testid="admin-refresh-btn"
              className="hidden sm:inline-flex wf-btn-ghost border border-neutral-200 rounded-full px-4 py-2 text-[12px] tracking-wide"
            >
              Aggiorna
            </button>
            <button
              type="button"
              onClick={onExportCsv}
              data-testid="admin-export-csv-btn"
              className="wf-btn-primary rounded-full px-5 py-2.5 text-[12px] tracking-wide"
            >
              Export CSV
            </button>
            <button
              type="button"
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="wf-btn-ghost border border-neutral-200 rounded-full px-4 py-2 text-[12px] tracking-wide"
            >
              Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1240px] mx-auto px-5 md:px-8 py-10 md:py-16">
        {/* Stats */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <Stat
            label="Totale iscritti"
            value={stats ? stats.total : "—"}
            testId="admin-stat-total"
          />
          <Stat
            label="Ultime 24h"
            value={stats ? stats.last_24h : "—"}
            testId="admin-stat-24h"
          />
          <Stat
            label="Ultimi 7 giorni"
            value={stats ? stats.last_7d : "—"}
            testId="admin-stat-7d"
          />
          <Stat
            label="Brevo sync"
            value={stats ? (stats.brevo_enabled ? "ON" : "OFF") : "—"}
            hint={
              stats?.brevo_enabled
                ? "Sincronizzazione attiva"
                : "Imposta BREVO_API_KEY e BREVO_LIST_ID"
            }
            testId="admin-stat-brevo"
          />
        </section>

        {/* Toolbar */}
        <section className="mt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h2 className="font-display text-[26px] md:text-[34px] tracking-[-0.02em] font-light text-neutral-950">
            Lista d'attesa
          </h2>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="admin-search-input"
              placeholder="Cerca email, source, status…"
              className="w-full md:w-80 bg-white border border-neutral-200 rounded-full px-5 py-2.5 text-[14px] outline-none focus:border-[color:var(--wf-gold)]"
            />
          </div>
        </section>

        {/* Table */}
        <section className="mt-6 border border-neutral-200 rounded-[12px] overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full text-[14px]" data-testid="admin-waitlist-table">
              <thead className="bg-[color:var(--wf-surface)] border-b border-neutral-200">
                <tr className="text-left text-[11px] tracking-[0.18em] uppercase text-neutral-500">
                  <th className="px-6 py-4 font-normal">Email</th>
                  <th className="px-6 py-4 font-normal">Source</th>
                  <th className="px-6 py-4 font-normal">Status</th>
                  <th className="px-6 py-4 font-normal">Iscritto il</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      data-testid="admin-empty-state"
                      className="px-6 py-12 text-center text-neutral-500"
                    >
                      {loading ? "Caricamento…" : "Nessun risultato"}
                    </td>
                  </tr>
                )}
                {filtered.map((e, i) => (
                  <tr
                    key={e.id || i}
                    data-testid={`admin-row-${i}`}
                    className="border-b border-neutral-100 last:border-0 hover:bg-[color:var(--wf-surface)] transition-colors"
                  >
                    <td className="px-6 py-4 text-neutral-950">{e.email}</td>
                    <td className="px-6 py-4 text-neutral-600">{e.source}</td>
                    <td className="px-6 py-4">
                      <span
                        className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] tracking-wide"
                        style={{
                          background: "rgba(201,168,106,0.08)",
                          border: "1px solid rgba(201,168,106,0.35)",
                          color: "#0a0a0a",
                        }}
                      >
                        <span
                          aria-hidden
                          className="w-1 h-1 rounded-full"
                          style={{ background: "#c9a86a" }}
                        />
                        {e.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-neutral-600 wf-tabular">
                      {fmt(e.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-3 border-t border-neutral-200 text-[12px] tracking-[0.14em] uppercase text-neutral-500 flex items-center justify-between">
            <span>
              {filtered.length} di {entries.length}
            </span>
            <span>WEDFLOW · privato</span>
          </div>
        </section>
      </main>
    </div>
  );
}
